import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, CheckCircle, XCircle, AlertCircle, Play, Download, ArrowLeft, ArrowRight, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useKommoImport, ColumnMapping } from '@/hooks/useKommoImport';
import Papa from 'papaparse';

type Step = 'upload' | 'mapping' | 'preview' | 'import';

// System fields that need to be mapped
const SYSTEM_FIELDS = [
  { key: 'id', label: 'ID Kommo', required: false },
  { key: 'nome', label: 'Nome do Contato', required: true },
  { key: 'telefone', label: 'Telefone', required: false },
  { key: 'email', label: 'E-mail', required: false },
  { key: 'birthDate', label: 'Data de Nascimento', required: false },
  { key: 'etapa', label: 'Etapa/Status', required: false },
  { key: 'googleDrive', label: 'Google Drive URL', required: false },
  { key: 'aprovacaoDocumentacao', label: 'Aprovação da Documentação', required: false },
  { key: 'biometria', label: 'Biometria Aplicante', required: false },
  { key: 'enderecoBiometria', label: 'Endereço Biometria', required: false },
  { key: 'aplicante', label: 'Aplicante (Nome)', required: false },
  { key: 'dependente1', label: 'Dependente 1', required: false },
  { key: 'biometriaDependente1', label: 'Biometria Dependente 1', required: false },
  { key: 'dependente2', label: 'Dependente 2', required: false },
  { key: 'biometriaDependente2', label: 'Biometria Dependente 2', required: false },
  { key: 'dependente3', label: 'Dependente 3', required: false },
  { key: 'biometriaDependente3', label: 'Biometria Dependente 3', required: false },
  { key: 'envioI589', label: 'Envio I-589', required: false },
  { key: 'envioI765', label: 'Envio I-765', required: false },
  { key: 'clock150Days', label: '150 Days Clock', required: false },
  { key: 'fillingReceived', label: 'Filling/Received', required: false },
  { key: 'eadSsn', label: 'Recebemos EAD/SSN', required: false },
  { key: 'nota1', label: 'Nota 1', required: false },
  { key: 'nota2', label: 'Nota 2', required: false },
  { key: 'nota3', label: 'Nota 3', required: false },
  { key: 'nota4', label: 'Nota 4', required: false },
  { key: 'nota5', label: 'Nota 5', required: false },
  { key: 'closingDate', label: 'Closing Date (Data Criação)', required: false },
];

export default function KommoImport() {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('upload');
  const [csvData, setCsvData] = useState<Record<string, string>[]>([]);
  const [csvColumns, setCsvColumns] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({});
  const [validationResult, setValidationResult] = useState<{
    valid: Record<string, string>[];
    invalid: { row: Record<string, string>; reason: string }[];
    alreadyImported: Record<string, string>[];
  } | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  
  const {
    isProcessing,
    results,
    progress,
    runImport,
    validateRows
  } = useKommoImport();

  // Auto-detect mapping based on column names - EXACT Kommo column names
  const autoDetectMapping = useCallback((columns: string[]): ColumnMapping => {
    const mapping: ColumnMapping = {};
    
    // Exact matches for Kommo CSV columns (case-insensitive)
    const EXACT_MATCHES: Record<string, keyof ColumnMapping> = {
      'id': 'id',
      'nome completo': 'nome',
      'nome completo:': 'nome',
      'lead título': 'nome',
      'nome do contato': 'nome',
      'birth date': 'birthDate',
      'birth date:': 'birthDate',
      'data de nascimento': 'birthDate',
      'etapa do lead': 'etapa',
      'google drive': 'googleDrive',
      'aprovação da documentação': 'aprovacaoDocumentacao',
      'aprovação da documentação:': 'aprovacaoDocumentacao',
      'biometria applicante:': 'biometria',
      'biometria applicante': 'biometria',
      'endereço biometria:': 'enderecoBiometria',
      'endereço biometria': 'enderecoBiometria',
      'aplicante:': 'aplicante',
      'aplicante': 'aplicante',
      'dependente 1:': 'dependente1',
      'dependente 1': 'dependente1',
      'dependente 2:': 'dependente2',
      'dependente 2': 'dependente2',
      'dependente 3:': 'dependente3',
      'dependente 3': 'dependente3',
      'biometria dependente 1:': 'biometriaDependente1',
      'biometria dependente 1': 'biometriaDependente1',
      'closing date': 'closingDate',
      'closing date:': 'closingDate',
      'data de criação': 'closingDate',
      'biometria dependente 2:': 'biometriaDependente2',
      'biometria dependente 2': 'biometriaDependente2',
      'biometria dependente 3:': 'biometriaDependente3',
      'biometria dependente 3': 'biometriaDependente3',
      'envio i-589': 'envioI589',
      'envio i-765': 'envioI765',
      '150 days clock': 'clock150Days',
      'filling/received': 'fillingReceived',
      'recebemos ead/ssn': 'eadSsn',
      'nota': 'nota1',
      'nota 2': 'nota2',
      'nota 3': 'nota3',
      'nota 4': 'nota4',
      'nota 5': 'nota5',
    };
    
    // Phone columns in priority order
    const PHONE_PRIORITY = ['telefone comercial', 'celular', 'outro telefone', 'telefone'];
    // Email columns in priority order
    const EMAIL_PRIORITY = ['email comercial', 'email pessoal', 'outro email', 'e-mail', 'email'];
    
    // First pass: exact matches
    columns.forEach((col) => {
      const lower = col.toLowerCase().trim();
      const exactMatch = EXACT_MATCHES[lower];
      if (exactMatch && !mapping[exactMatch]) {
        mapping[exactMatch] = col;
      }
    });
    
    // Second pass: phone with priority
    for (const phoneKey of PHONE_PRIORITY) {
      if (mapping.telefone) break;
      const found = columns.find(col => col.toLowerCase().trim() === phoneKey);
      if (found) mapping.telefone = found;
    }
    
    // Third pass: email with priority
    for (const emailKey of EMAIL_PRIORITY) {
      if (mapping.email) break;
      const found = columns.find(col => col.toLowerCase().trim() === emailKey);
      if (found) mapping.email = found;
    }
    
    // Fallback fuzzy matching for fields not yet mapped
    columns.forEach((col) => {
      const lower = col.toLowerCase().trim();
      
      if (!mapping.id && (lower === 'id' || lower.includes('lead id'))) {
        mapping.id = col;
      }
      if (!mapping.nome && (lower.includes('nome') || lower.includes('name'))) {
        mapping.nome = col;
      }
      if (!mapping.telefone && (lower.includes('telefone') || lower.includes('phone') || lower.includes('fone'))) {
        mapping.telefone = col;
      }
      if (!mapping.email && (lower.includes('email') || lower.includes('e-mail'))) {
        mapping.email = col;
      }
      if (!mapping.etapa && (lower.includes('etapa') || lower.includes('stage') || lower.includes('status'))) {
        mapping.etapa = col;
      }
      if (!mapping.googleDrive && (lower.includes('google') || lower.includes('drive'))) {
        mapping.googleDrive = col;
      }
    });
    
    return mapping;
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setFileName(file.name);
    
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data as Record<string, string>[];
        const columns = results.meta.fields || [];
        
        console.log('CSV Columns detected:', columns);
        console.log('First row sample:', data[0]);
        
        setCsvData(data);
        setCsvColumns(columns);
        
        // Auto-detect column mapping
        const autoMapping = autoDetectMapping(columns);
        console.log('Auto-detected mapping:', autoMapping);
        setColumnMapping(autoMapping);
        
        setStep('mapping');
      },
      error: (error) => {
        console.error('CSV parse error:', error);
      }
    });
  }, [autoDetectMapping]);

  // Handle mapping change
  const handleMappingChange = useCallback((field: string, csvColumn: string) => {
    setColumnMapping(prev => ({
      ...prev,
      [field]: csvColumn === '__none__' ? undefined : csvColumn
    }));
  }, []);

  // Check if mapping is valid (at least nome is mapped)
  const isMappingValid = useMemo(() => {
    return !!columnMapping.nome;
  }, [columnMapping.nome]);

  // Go to preview step
  const handleGoToPreview = useCallback(() => {
    setStep('preview');
    setValidationResult(null);
  }, []);

  // Handle validation
  const handleValidate = useCallback(async () => {
    setIsValidating(true);
    try {
      const result = await validateRows(csvData, columnMapping);
      setValidationResult(result);
    } finally {
      setIsValidating(false);
    }
  }, [csvData, columnMapping, validateRows]);

  // Handle import start
  const handleStartImport = useCallback(async () => {
    if (!validationResult) return;
    setStep('import');
    await runImport(validationResult.valid, columnMapping);
  }, [validationResult, columnMapping, runImport]);

  // Download results as JSON
  const handleDownloadResults = useCallback(() => {
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kommo-import-results-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [results]);

  // Reset to start
  const handleReset = useCallback(() => {
    setStep('upload');
    setCsvData([]);
    setCsvColumns([]);
    setFileName('');
    setColumnMapping({});
    setValidationResult(null);
  }, []);

  // Get value from row using mapping
  const getMappedValue = useCallback((row: Record<string, string>, field: keyof ColumnMapping): string => {
    const csvColumn = columnMapping[field];
    if (!csvColumn) return '-';
    return row[csvColumn] || '-';
  }, [columnMapping]);

  // Stats
  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error').length;
  const skippedCount = results.filter(r => r.status === 'skipped').length;

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Import Kommo - Asilo</h1>
          <p className="text-muted-foreground">
            Importe processos do pipeline Asilo do Kommo
          </p>
        </div>
        
        {/* Step indicator */}
        <div className="flex items-center gap-2">
          <Badge variant={step === 'upload' ? 'default' : 'secondary'}>1. Upload</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <Badge variant={step === 'mapping' ? 'default' : 'secondary'}>2. Mapeamento</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <Badge variant={step === 'preview' ? 'default' : 'secondary'}>3. Preview</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <Badge variant={step === 'import' ? 'default' : 'secondary'}>4. Import</Badge>
        </div>
      </div>

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload CSV
            </CardTitle>
            <CardDescription>
              Selecione o arquivo CSV exportado do Kommo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-12 text-center hover:border-muted-foreground/50 transition-colors">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">Arraste o arquivo CSV aqui</p>
                <p className="text-sm text-muted-foreground mt-1">ou clique para selecionar</p>
              </label>
            </div>
            
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-2">O sistema detectará automaticamente as colunas e você poderá mapeá-las</h4>
              <p className="text-sm text-muted-foreground">
                Após o upload, você poderá indicar qual coluna do CSV corresponde a cada campo do sistema.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Column Mapping */}
      {step === 'mapping' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Settings2 className="h-5 w-5" />
                  Mapeamento de Colunas: {fileName}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleReset}>
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Voltar
                  </Button>
                  <Button onClick={handleGoToPreview} disabled={!isMappingValid}>
                    <ArrowRight className="h-4 w-4 mr-1" />
                    Próximo
                  </Button>
                </div>
              </CardTitle>
              <CardDescription>
                {csvColumns.length} colunas encontradas no CSV. Mapeie cada coluna para o campo correspondente.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Mapping summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium text-green-600">
                      {Object.values(columnMapping).filter(Boolean).length} mapeados
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                    <span className="text-sm font-medium text-yellow-600">
                      {SYSTEM_FIELDS.length - Object.values(columnMapping).filter(Boolean).length} não mapeados
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-muted border rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">
                      {csvColumns.length} colunas no CSV
                    </span>
                  </div>
                </div>
              </div>

              {/* Columns detected */}
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2">Colunas detectadas no CSV:</h4>
                <div className="flex flex-wrap gap-2">
                  {csvColumns.map(col => {
                    const isMapped = Object.values(columnMapping).includes(col);
                    return (
                      <Badge 
                        key={col} 
                        variant={isMapped ? "default" : "outline"}
                        className={isMapped ? "bg-green-500" : ""}
                      >
                        {isMapped && <CheckCircle className="h-3 w-3 mr-1" />}
                        {col}
                      </Badge>
                    );
                  })}
                </div>
              </div>

              {/* Mapping form */}
              <div className="grid grid-cols-2 gap-4">
                {SYSTEM_FIELDS.map(field => {
                  const mappedColumn = columnMapping[field.key as keyof ColumnMapping];
                  const isMapped = !!mappedColumn;
                  
                  return (
                    <div 
                      key={field.key} 
                      className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                        isMapped ? 'bg-green-500/5 border-green-500/30' : 
                        field.required ? 'bg-destructive/5 border-destructive/30' : ''
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {isMapped ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : field.required ? (
                            <XCircle className="h-4 w-4 text-destructive" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-muted-foreground" />
                          )}
                          <p className="font-medium text-sm">
                            {field.label}
                            {field.required && <span className="text-destructive ml-1">*</span>}
                          </p>
                        </div>
                      </div>
                      <Select
                        value={mappedColumn || '__none__'}
                        onValueChange={(value) => handleMappingChange(field.key, value)}
                      >
                        <SelectTrigger className={`w-[220px] ${isMapped ? 'border-green-500/50' : ''}`}>
                          <SelectValue placeholder="Selecione coluna" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">-- Não mapear --</SelectItem>
                          {csvColumns.map(col => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>

              {!isMappingValid && (
                <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-destructive" />
                  <p className="text-sm text-destructive">
                    O campo "Nome do Contato" é obrigatório. Por favor, mapeie uma coluna para ele.
                  </p>
                </div>
              )}

              {/* Preview sample with mapping */}
              <div>
                <h4 className="font-medium mb-2">Amostra dos dados (primeiras 3 linhas):</h4>
                <div className="overflow-x-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">#</TableHead>
                        {SYSTEM_FIELDS.filter(f => columnMapping[f.key as keyof ColumnMapping]).map(field => (
                          <TableHead key={field.key} className="min-w-[120px]">
                            {field.label}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {csvData.slice(0, 3).map((row, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-mono text-xs">{index + 1}</TableCell>
                          {SYSTEM_FIELDS.filter(f => columnMapping[f.key as keyof ColumnMapping]).map(field => (
                            <TableCell key={field.key} className="text-sm">
                              {getMappedValue(row, field.key as keyof ColumnMapping)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 3: Preview */}
      {step === 'preview' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Preview: {fileName}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setStep('mapping')}>
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Voltar
                  </Button>
                  {!validationResult && (
                    <Button onClick={handleValidate} disabled={isValidating}>
                      {isValidating ? 'Validando...' : 'Validar dados'}
                    </Button>
                  )}
                </div>
              </CardTitle>
              <CardDescription>
                {csvData.length} linhas encontradas no arquivo
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Validation results */}
              {validationResult && (
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <Card className="bg-green-500/10 border-green-500/20">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <div>
                          <p className="text-2xl font-bold text-green-500">{validationResult.valid.length}</p>
                          <p className="text-sm text-muted-foreground">Válidos</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-yellow-500/10 border-yellow-500/20">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-yellow-500" />
                        <div>
                          <p className="text-2xl font-bold text-yellow-500">{validationResult.alreadyImported.length}</p>
                          <p className="text-sm text-muted-foreground">Já importados</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-500/10 border-red-500/20">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2">
                        <XCircle className="h-5 w-5 text-red-500" />
                        <div>
                          <p className="text-2xl font-bold text-red-500">{validationResult.invalid.length}</p>
                          <p className="text-sm text-muted-foreground">Inválidos</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Full CSV data table with all columns */}
              <div className="border rounded-lg overflow-hidden">
                <ScrollArea className="h-[400px]">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px] sticky left-0 bg-background">#</TableHead>
                          {csvColumns.map(col => (
                            <TableHead 
                              key={col} 
                              className={`min-w-[120px] ${Object.values(columnMapping).includes(col) ? 'bg-primary/10' : ''}`}
                            >
                              {col}
                              {Object.values(columnMapping).includes(col) && (
                                <Badge variant="secondary" className="ml-1 text-xs">mapeado</Badge>
                              )}
                            </TableHead>
                          ))}
                          {validationResult && <TableHead className="min-w-[100px]">Status</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {csvData.slice(0, 100).map((row, index) => {
                          const rowId = columnMapping.id ? row[columnMapping.id] : `row-${index}`;
                          const isInvalid = validationResult?.invalid.some(i => {
                            const iId = columnMapping.id ? i.row[columnMapping.id] : null;
                            return iId === rowId;
                          });
                          const isAlreadyImported = validationResult?.alreadyImported.some(i => {
                            const iId = columnMapping.id ? i[columnMapping.id] : null;
                            return iId === rowId;
                          });
                          const invalidReason = validationResult?.invalid.find(i => {
                            const iId = columnMapping.id ? i.row[columnMapping.id] : null;
                            return iId === rowId;
                          })?.reason;
                          
                          return (
                            <TableRow 
                              key={index} 
                              className={isInvalid ? 'bg-red-500/5' : isAlreadyImported ? 'bg-yellow-500/5' : ''}
                            >
                              <TableCell className="font-mono text-xs sticky left-0 bg-background">{index + 1}</TableCell>
                              {csvColumns.map(col => (
                                <TableCell 
                                  key={col} 
                                  className={`text-sm ${Object.values(columnMapping).includes(col) ? 'bg-primary/5' : ''}`}
                                >
                                  {row[col] || '-'}
                                </TableCell>
                              ))}
                              {validationResult && (
                                <TableCell>
                                  {isInvalid ? (
                                    <Badge variant="destructive" className="text-xs">{invalidReason}</Badge>
                                  ) : isAlreadyImported ? (
                                    <Badge variant="secondary" className="text-xs">Já importado</Badge>
                                  ) : (
                                    <Badge variant="default" className="text-xs bg-green-500">OK</Badge>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </ScrollArea>
              </div>
              
              {csvData.length > 100 && (
                <p className="text-sm text-muted-foreground mt-2">
                  Mostrando 100 de {csvData.length} linhas
                </p>
              )}

              {/* Start import button */}
              {validationResult && validationResult.valid.length > 0 && (
                <div className="mt-6 flex justify-end">
                  <Button size="lg" onClick={handleStartImport}>
                    <Play className="h-4 w-4 mr-2" />
                    Importar {validationResult.valid.length} registros
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 4: Import */}
      {step === 'import' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {isProcessing ? (
                  <>
                    <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    Importação concluída
                  </>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Progress */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progresso</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} className="h-3" />
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-green-500/10 border-green-500/20">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <div>
                        <p className="text-2xl font-bold text-green-500">{successCount}</p>
                        <p className="text-sm text-muted-foreground">Sucesso</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-yellow-500/10 border-yellow-500/20">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-yellow-500" />
                      <div>
                        <p className="text-2xl font-bold text-yellow-500">{skippedCount}</p>
                        <p className="text-sm text-muted-foreground">Pulados</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-red-500/10 border-red-500/20">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <XCircle className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="text-2xl font-bold text-red-500">{errorCount}</p>
                        <p className="text-sm text-muted-foreground">Erros</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Results log */}
              <div>
                <h4 className="font-medium mb-2">Log de importação (últimos 20)</h4>
                <ScrollArea className="h-[300px] rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">#</TableHead>
                        <TableHead>ID Kommo</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Mensagem</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {results.slice(-20).reverse().map((result, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-mono text-xs">{result.rowNumber}</TableCell>
                          <TableCell className="font-mono text-xs">{result.kommoLeadId}</TableCell>
                          <TableCell>
                            {result.status === 'success' && (
                              <Badge className="bg-green-500">Sucesso</Badge>
                            )}
                            {result.status === 'error' && (
                              <Badge variant="destructive">Erro</Badge>
                            )}
                            {result.status === 'skipped' && (
                              <Badge variant="secondary">Pulado</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{result.message}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>

              {/* Actions */}
              {!isProcessing && (
                <div className="flex justify-between">
                  <Button variant="outline" onClick={handleReset}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Nova importação
                  </Button>
                  <Button onClick={handleDownloadResults}>
                    <Download className="h-4 w-4 mr-2" />
                    Baixar log completo
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
